import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, FileText, CalendarDays, Receipt, Truck, AlertCircle, ChevronRight } from "lucide-react";
import api from "@/lib/api";

const CARDS = [
  { key: "new_requests", label: "Neue Anfragen", icon: Inbox, to: "/crm/anfragen", color: "text-[#6B7F3E]" },
  { key: "open_quotes", label: "Offene Angebote", icon: FileText, to: "/crm/angebote", color: "text-[#B85C38]" },
  { key: "upcoming_appointments", label: "Termine (7 Tage)", icon: CalendarDays, to: "/crm/termine", color: "text-[#2C3E2D]" },
  { key: "unpaid_invoices", label: "Unbezahlte Rechnungen", icon: Receipt, to: "/crm/rechnungen", color: "text-[#F59E0B]" },
  { key: "running_tours", label: "Laufende Touren", icon: Truck, to: "/crm/termine", color: "text-[#4A5D4E]" },
  { key: "overdue_invoices", label: "Überfällig", icon: AlertCircle, to: "/crm/rechnungen", color: "text-[#B85C38]" },
];

const STATUS_LABEL = {
  neu: "Neu",
  in_bearbeitung: "In Bearbeitung",
  angebot_versendet: "Angebot versendet",
  angenommen: "Angenommen",
  abgelehnt: "Abgelehnt",
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats")
      .then((r) => setStats(r.data))
      .catch((e) => setError(e?.message || "Fehler"));
  }, []);

  return (
    <div data-testid="crm-dashboard">
      <div className="mb-5 md:mb-8">
        <div className="hidden md:block text-xs font-medium text-[#6B7F3E] uppercase tracking-wider mb-2">Übersicht</div>
        <h1 className="hidden md:block font-display text-3xl sm:text-4xl font-semibold text-[#1A201A] tracking-tight">
          Dashboard
        </h1>
        <p className="text-sm text-[#5C6B5D] mt-1">
          Alles Wichtige auf einen Blick. Willkommen zurück.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 mb-6 md:mb-10">
        {CARDS.map((c) => {
          const Icon = c.icon;
          const value = stats ? stats[c.key] : "…";
          return (
            <Link
              key={c.key}
              to={c.to}
              data-testid={`kpi-${c.key.replace(/_/g, "-")}`}
              className="bg-white border border-[#E5E7EB] rounded-xl p-4 md:p-5 hover:border-[#6B7F3E]/50 transition-colors group"
            >
              <div className="flex items-start justify-between mb-3">
                <div className={`w-9 h-9 rounded-lg bg-[#F5F6F0] flex items-center justify-center ${c.color}`}>
                  <Icon className="w-4 h-4" strokeWidth={1.5} />
                </div>
                <ChevronRight className="w-4 h-4 text-[#C4CFB1] group-hover:text-[#6B7F3E] group-hover:translate-x-0.5 transition-all" />
              </div>
              <div className="font-display text-3xl font-semibold text-[#1A201A]">{value}</div>
              <div className="text-xs text-[#5C6B5D] mt-1">{c.label}</div>
            </Link>
          );
        })}
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-xl p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-lg font-medium text-[#1A201A]">Letzte Anfragen</h2>
          <Link
            to="/crm/anfragen"
            className="text-xs font-medium text-[#6B7F3E] hover:underline"
            data-testid="dashboard-view-all-requests"
          >
            Alle anzeigen →
          </Link>
        </div>
        {error && <div className="text-sm text-[#B85C38]">{error}</div>}
        {!stats && !error && <div className="text-sm text-[#5C6B5D]">Lädt…</div>}
        {stats && stats.recent_requests.length === 0 && (
          <div className="text-sm text-[#5C6B5D] py-6 text-center">
            Noch keine Anfragen vorhanden.
          </div>
        )}
        {stats && stats.recent_requests.length > 0 && (
          <div className="divide-y divide-[#E5E7EB]">
            {stats.recent_requests.map((r) => (
              <div key={r.id} className="py-3 flex items-center gap-4">
                <div className={`w-1 h-10 rounded-full ${r.dringlichkeit === "hoch" ? "bg-[#B85C38]" : r.dringlichkeit === "mittel" ? "bg-[#F59E0B]" : "bg-[#C4CFB1]"}`} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-[#1A201A]">{r.customer_name}</div>
                  <div className="text-xs text-[#5C6B5D] truncate">
                    {r.objekt_art === "gewerbe" ? "Gewerbe · " : "Privat · "}
                    {r.leistungen.slice(0, 3).join(", ")}
                  </div>
                </div>
                <span className="text-xs px-2 py-0.5 rounded-full bg-[#F5F6F0] text-[#2C3E2D]">
                  {STATUS_LABEL[r.status] || r.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
